// Batch E1.5 — Stage badge.
//
// Two modes:
//   editable=false  → compact pill (bg-token + text-token + label) for tables.
//   editable=true   → click-to-open shadcn DropdownMenu with all 8 stages.
//                     Optimistic UI; reverts + toasts on PATCH failure.
//
// Stage metadata is sourced ONCE from GET /api/pipeline/stages and cached
// at module scope so a Portfolio with 480 rows hits the network exactly
// once total, not 480x.

import { useEffect, useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Check, ChevronDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { invalidatePipelineCounts } from "@/lib/usePipelineCounts";

// Phase 36 Dashboard P0 follow-up #6 — fallback stage list.
//
// `loadStages()` previously used raw `axios.get(...)` which bypassed the
// centralised 401 auto-retry interceptor. If the call failed during the
// first-load auth race, `STAGES_CACHE` stayed null forever, every
// consumer of `useStages()` got `[]`, and Pipeline.jsx ended up with
// `orderedColumns = []` → empty board even though /pipeline/board had
// actually returned data. We now (a) route through the shared `api`
// instance so the interceptor's 1.5 s 401 retry applies, and (b) seed
// the cache with a hard-coded fallback that mirrors the backend's
// canonical 8-stage list so even a persistent network failure can't
// produce an empty board.
const FALLBACK_STAGES = [
  { id: "prospect",        label: "Prospect",        colors: { bg: "bg-slate-100",   text: "text-slate-700"   } },
  { id: "watching",        label: "Watching",        colors: { bg: "bg-blue-100",    text: "text-blue-700"    } },
  { id: "offer_submitted", label: "Offer Submitted", colors: { bg: "bg-amber-100",   text: "text-amber-700"   } },
  { id: "negotiating",     label: "Negotiating",     colors: { bg: "bg-orange-100",  text: "text-orange-700"  } },
  { id: "under_contract",  label: "Under Contract",  colors: { bg: "bg-violet-100",  text: "text-violet-700"  } },
  { id: "owned",           label: "Owned",           colors: { bg: "bg-emerald-100", text: "text-emerald-700" } },
  { id: "sold",            label: "Sold",            colors: { bg: "bg-indigo-100",  text: "text-indigo-700"  } },
  { id: "passed",          label: "Passed",          colors: { bg: "bg-rose-100",    text: "text-rose-700"    } },
];

// Module-level cache — populated by the first <StageBadge /> mounted on
// the page and shared with every sibling instance.
let STAGES_CACHE = null;
let STAGES_PROMISE = null;

async function loadStages() {
  if (STAGES_CACHE) return STAGES_CACHE;
  if (!STAGES_PROMISE) {
    STAGES_PROMISE = api
      .get("/pipeline/stages")
      .then((r) => {
        STAGES_CACHE = r.data;
        return STAGES_CACHE;
      })
      .catch((e) => {
        // Last-resort: use the hard-coded list so the UI keeps rendering.
        // The interceptor in api.js has already silently retried once on
        // 401, so if we get here the failure is persistent (e.g. network
        // truly down). Seed the cache so we don't keep retrying.
        STAGES_CACHE = FALLBACK_STAGES;
        STAGES_PROMISE = null;
        throw e;
      });
  }
  return STAGES_PROMISE;
}

export function useStages() {
  const [stages, setStages] = useState(STAGES_CACHE);
  useEffect(() => {
    if (!stages) {
      loadStages()
        .then(setStages)
        .catch(() => {
          // loadStages() seeded STAGES_CACHE = FALLBACK_STAGES on failure;
          // mirror that into local state so this consumer re-renders.
          setStages(STAGES_CACHE || FALLBACK_STAGES);
        });
    }
  }, [stages]);
  // Belt-and-braces: never return `[]` from a successful resolution path.
  return (stages && stages.length > 0) ? stages : (STAGES_CACHE || FALLBACK_STAGES);
}

function stageMeta(stages, id) {
  return stages.find((s) => s.id === id);
}

export default function StageBadge({
  stage,
  size = "sm",
  editable = false,
  onChange,
  dealId,
  className = "",
}) {
  const stages = useStages();
  const [current, setCurrent] = useState(stage);
  const [busy, setBusy] = useState(false);

  // Keep local in sync with prop (e.g. parent refetch).
  useEffect(() => setCurrent(stage), [stage]);

  const meta = stageMeta(stages, current) || {
    id: current || "prospect",
    label: current ? current.replace(/_/g, " ") : "—",
    colors: { bg: "bg-slate-100", text: "text-slate-500" },
  };

  const sizes = {
    sm: "px-2 py-0.5 text-[11px] gap-1",
    md: "px-2.5 py-1 text-xs gap-1.5",
  };
  const sizeClass = sizes[size] || sizes.sm;
  const dot = `${meta.colors.bg.replace("bg-", "bg-").replace("-100", "-500")}`;

  const pill = (
    <span
      data-testid={`stage-badge-${dealId || meta.id}`}
      data-stage={meta.id}
      className={`inline-flex items-center whitespace-nowrap rounded-full font-medium ${meta.colors.bg} ${meta.colors.text} ${sizeClass} ${className}`}
    >
      <span aria-hidden="true" className={`h-1.5 w-1.5 rounded-full ${dot}`} />
      <span className="truncate">{meta.label}</span>
      {busy && <Loader2 className="ml-0.5 h-3 w-3 animate-spin" />}
      {editable && !busy && (
        <ChevronDown className="ml-0.5 h-3 w-3 opacity-60" />
      )}
    </span>
  );

  if (!editable) return pill;

  async function applyStage(newStageId) {
    if (newStageId === current || busy) return;
    const prev = current;
    setBusy(true);
    setCurrent(newStageId); // optimistic
    try {
      // Phase 36 P0 follow-up #6 — use the shared `api` instance so the
      // 401 interceptor's auto-retry applies to PATCHes too (same race
      // can affect the very first stage edit after login).
      await api.patch(
        `/deals/${dealId}/stage`,
        { stage: newStageId, note: null }
      );
      invalidatePipelineCounts();
      toast.success(
        `Moved to ${stageMeta(stages, newStageId)?.label || newStageId}`
      );
      onChange && onChange(newStageId);
    } catch (e) {
      setCurrent(prev); // revert
      toast.error(
        e?.response?.data?.detail || "Could not update stage. Try again."
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid={`stage-badge-trigger-${dealId}`}
          disabled={busy}
          onClick={(e) => e.stopPropagation()}
          className="inline-flex"
        >
          {pill}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-56"
        data-testid={`stage-menu-${dealId}`}
      >
        {stages.map((s) => (
          <DropdownMenuItem
            key={s.id}
            data-testid={`stage-option-${s.id}-${dealId}`}
            onSelect={() => {
              // Phase 36 — Dashboard P0 follow-up #5 (mobile Safari fix).
              //
              // We DELIBERATELY do NOT call `e.preventDefault()` here.
              // Per Radix docs, `preventDefault()` keeps the menu open
              // after a selection. Previously this caused the optimistic
              // chip update to be hidden BEHIND the still-open dropdown
              // on mobile, making it look like "tap does nothing" — the
              // PATCH was firing, the chip was updating, the menu was
              // just obscuring the result. Letting Radix default-close
              // the menu after `onSelect` makes the optimistic update
              // visible immediately.
              applyStage(s.id);
            }}
            className="cursor-pointer"
          >
            <span
              aria-hidden="true"
              className={`mr-2 h-2 w-2 rounded-full ${s.colors.bg.replace("-100", "-500")}`}
            />
            <span className={`flex-1 ${s.colors.text}`}>{s.label}</span>
            {s.id === current && (
              <Check className="ml-2 h-3.5 w-3.5 text-indigo-600" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
